import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  CircularProgress,
  Chip,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CardContent,
  Divider,
  Card,
  IconButton,
} from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { useSelector } from "react-redux";
import configServ from "../../services/config";
import CardHeadMenu from "../../Components/Card/CardHeadMenu/CardHeadMenu";
import CustomDateRangePicker from "../../Components/Card/DatePicker/CustomDateRangePicker";
import dayjs from "dayjs";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import * as XLSX from "xlsx";
import Swal from "sweetalert2";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import AttendanceCalendarDialog from "./AttendanceCalendarDialog";
import ExcelImg from "../../Components/Assets/images/Excel.jpg";
import pdfImg from "../../Components/Assets/images/pdf.png";
import attImage from "../../Components/Assets/images//attendanceImage.png";
import info from "../../Components/Assets/images/info.png";

import { downloadExcelReport } from "../../Components/Download/downloadExcelReport";

const AttendanceReport = () => {
  const { company_id } = useSelector((state) => state.GlobalVariables);

  // 📌 State
  const [loading, setLoading] = useState(false);
  const [students, setStudents] = useState([]);
  const [batches, setBatches] = useState([]);
  const [total, setTotal] = useState(0);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [sortField, setSortField] = useState("full_name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [searchType, setSearchType] = useState("full_name");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedBatch, setSelectedBatch] = useState("All");

  const [calendarDialog, setCalendarDialog] = useState(false);
  const [selectedCalendar, setSelectedCalendar] = useState([]);
  const [dateRange, setDateRange] = useState([null, null]);

  const [batchDays, setBatchDays] = useState(null);
  const [dayRange, setDayRange] = useState(null);

  // 📌 Table Columns
  const headers = [
    // { label: "Profile", value: "profile" },
    { label: "Student Name", value: "full_name" },
    { label: "Reg No.", value: "registration_no" },
    { label: "Roll No.", value: "roll_no" },
    { label: "Present", value: "present" },
    { label: "Absent", value: "absent" },
    { label: "Total Classes", value: "total_classes" },
    { label: "Attendance %", value: "attendance_percentage" },
  ];

  // 📌 Handle Sort
  const handleSort = (field) => {
    if (
      field === "present" ||
      field === "absent" ||
      field === "total_classes" ||
      field === "attendance_percentage"
      //   field === "profile"
    )
      return; // ignore action column
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  // 📌 Handle Pagination
  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleReset = () => {
    setSearchTerm("");
    setSearchType("full_name");
    setSelectedBatch("All");
    setSortField("full_name");
    setSortOrder("asc");
  };

  // 📌 Fetch Attendance Report
  const fetchAttendanceReport = async () => {
    try {
      setLoading(true);
      const dataToSend = {
        company_id,
        batch_id: selectedBatch !== "All" ? selectedBatch : null,
        page: page + 1,
        per_page: rowsPerPage,
        search: searchTerm,
        search_by: searchType,
        sort_field: sortField,
        sort_order: sortOrder,
        from_date: dateRange[0] ? dayjs(dateRange[0]).format("YYYY-MM-DD") : "",
        to_date: dateRange[1] ? dayjs(dateRange[1]).format("YYYY-MM-DD") : "",
      };

      const res = await configServ.getAttendanceReport(dataToSend);

      if (res?.status === true) {
        setStudents(res.students || []);
        setTotal(res.pagination?.total_records || 0);
        setBatchDays(res.batch_active_days || null);
        setDayRange(res.date_range || null);
      } else {
        setStudents([]);
        setTotal(0);
      }
    } catch (error) {
      console.error("Error fetching attendance report:", error);
      setStudents([]);
    } finally {
      setLoading(false);
    }
  };

  // 📌 Fetch Active Batches
  const fetchBatches = async () => {
    try {
      const result = await configServ.getActiveBatches({ company_id });
      if (result?.status === true) {
        setBatches(result.data);
      }
    } catch (error) {
      console.error("Error fetching batches:", error);
    }
  };

  // 📌 Open Calendar Dialog
  const handleViewCalendar = (calendar) => {
    setSelectedCalendar(calendar);
    setCalendarDialog(true);
  };

  // 📌 Auto fetch on filter/sort change
  useEffect(() => {
    fetchAttendanceReport();
  }, [
    selectedBatch,
    searchTerm,
    searchType,
    rowsPerPage,
    page,
    sortField,
    sortOrder,
    dateRange,
  ]);

  useEffect(() => {
    fetchBatches();
  }, []);

  // 📌 Styles
  const headerCellStyle = {
    fontSize: 14,
    fontWeight: "bold",
    backgroundColor: "#010911ff",
    color: "white",
    padding: 8,
    textAlign: "center",
  };
  const cellStyle = { py: 0.5, textAlign: "center" };
  const rowStyle = { "&:hover": { background: "#f5faff" } };

  //   const downloadAttendanceExcel = async () => {
  //     try {
  //       // Show loader while fetching
  //       Swal.fire({
  //         title: "Generating Excel...",
  //         text: "Please wait while we prepare your report.",
  //         allowOutsideClick: false,
  //         didOpen: () => Swal.showLoading(),
  //       });

  //       // 🔹 Prepare payload (same as fetchAttendanceReport)
  //       const dataToSend = {
  //         company_id,
  //         batch_id: selectedBatch !== "All" ? selectedBatch : null,
  //         page: page + 1,
  //         per_page: rowsPerPage,
  //         search: searchTerm,
  //         search_by: searchType,
  //         sort_field: sortField,
  //         sort_order: sortOrder,
  //         from_date: dateRange[0] ? dayjs(dateRange[0]).format("YYYY-MM-DD") : "",
  //         to_date: dateRange[1] ? dayjs(dateRange[1]).format("YYYY-MM-DD") : "",
  //       };

  //       // 🔹 Call API
  //       const res = await configServ.getAttendanceReport(dataToSend);

  //       Swal.close(); // close loader

  //       if (!res?.status || !res?.students?.length) {
  //         Swal.fire(
  //           "No Data",
  //           "No attendance data available to download",
  //           "info"
  //         );
  //         return;
  //       }

  //       // 🔹 Format data (ignore calendar)
  //       const formattedData = res.students.map((student) => ({
  //         STUDENT_NAME: student.student_name,
  //         REG_NO: student.registration_no,
  //         ROLL_NO: student.roll_no,
  //         PRESENT: student.present,
  //         ABSENT: student.absent,
  //         TOTAL_CLASSES: student.total_classes,
  //         ATTENDANCE_PERCENTAGE: `${student.attendance_percentage}%`,
  //       }));

  //       // 🔹 Create sheet
  //       const worksheet = XLSX.utils.json_to_sheet([]);
  //       const workbook = XLSX.utils.book_new();

  //       // Add title and batch info
  //       XLSX.utils.sheet_add_aoa(
  //         worksheet,
  //         [
  //           ["ATTENDANCE REPORT"],
  //           [`Batch Name: ${res.batch_name || "—"}`],
  //           [`Date Range: ${res.date_range?.join(" to ") || "—"}`],
  //           [],
  //         ],
  //         { origin: "A1" }
  //       );

  //       // Add data from row 5
  //       XLSX.utils.sheet_add_json(worksheet, formattedData, { origin: "A5" });

  //       // Set column widths
  //       worksheet["!cols"] = [
  //         { wch: 40 },
  //         { wch: 40 },
  //         { wch: 10 },
  //         { wch: 10 },
  //         { wch: 10 },
  //         { wch: 15 },
  //         { wch: 25 },
  //       ];

  //       // Append and download
  //       XLSX.utils.book_append_sheet(workbook, worksheet, "Attendance Report");
  //       XLSX.writeFile(workbook, "attendance_report.xlsx");

  //       Swal.fire({
  //         position: "top-end",
  //         icon: "success",
  //         title: "Excel downloaded successfully",
  //         showConfirmButton: false,
  //         timer: 1500,
  //       });
  //     } catch (error) {
  //       Swal.close();
  //       console.error("Excel download error:", error);
  //       Swal.fire("Error", "Failed to download Excel file", "error");
  //     }
  //   };

  const downloadAttendancePDF = async () => {
    try {
      Swal.fire({
        title: "Generating PDF...",
        text: "Please wait while we prepare your report.",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      const dataToSend = {
        company_id,
        batch_id: selectedBatch !== "All" ? selectedBatch : null,
        page: page + 1,
        per_page: rowsPerPage,
        search: searchTerm,
        search_by: searchType,
        sort_field: sortField,
        sort_order: sortOrder,
        from_date: dateRange[0] ? dayjs(dateRange[0]).format("YYYY-MM-DD") : "",
        to_date: dateRange[1] ? dayjs(dateRange[1]).format("YYYY-MM-DD") : "",
      };

      const res = await configServ.getAttendanceReport(dataToSend);
      Swal.close();

      if (!res?.status || !res?.students?.length) {
        Swal.fire(
          "No Data",
          "No attendance data available to download",
          "info"
        );
        return;
      }

      const formattedData = res.students.map((student) => [
        student.student_name,
        student.registration_no,
        student.roll_no,
        student.present,
        student.absent,
        student.total_classes,
        `${student.attendance_percentage}%`,
      ]);

      const doc = new jsPDF();

      doc.setFontSize(16);
      doc.text("ATTENDANCE REPORT", 70, 15);

      doc.setFontSize(12);
      doc.text(`Batch Name: ${res.batch_name || "—"}`, 14, 25);
      doc.text(`Date Range: ${res.date_range?.join(" to ") || "—"}`, 14, 32);

      autoTable(doc, {
        head: [
          [
            "STUDENT NAME",
            "Reg No",
            "Roll No.",
            "PRESENT",
            "ABSENT",
            "TOTAL CLASSES",
            "ATTENDANCE %",
          ],
        ],
        body: formattedData,
        startY: 40,
      });

      doc.save("attendance_report.pdf");

      Swal.fire({
        position: "top-end",
        icon: "success",
        title: "PDF downloaded successfully",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (error) {
      Swal.close();
      console.error("PDF download error:", error);
      Swal.fire("Error", "Failed to download PDF file", "error");
    }
  };

  const handleInfo = async () => {
    try {
      Swal.fire({
        title: "Fetching Info...",
        text: "Please wait while we load batch details.",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      const dataToSend = {
        company_id,
        batch_id: selectedBatch !== "All" ? selectedBatch : null,
        page: page + 1,
        per_page: rowsPerPage,
        search: searchTerm,
        search_by: searchType,
        sort_field: sortField,
        sort_order: sortOrder,
        from_date: dateRange[0] ? dayjs(dateRange[0]).format("YYYY-MM-DD") : "",
        to_date: dateRange[1] ? dayjs(dateRange[1]).format("YYYY-MM-DD") : "",
      };

      const res = await configServ.getAttendanceReport(dataToSend);
      Swal.close();

      if (!res?.status) {
        Swal.fire("Error", "Failed to fetch batch information", "error");
        return;
      }

      // 🟢 Show only Batch Info
      const batchInfoHtml = `
      <div style="text-align:left; font-size:14px; line-height:1.6;">
        <b>Batch Name:</b> ${res.batch_name || "—"}<br/>
        <b>Date Range:</b> ${res.date_range?.join(" to ") || "—"}<br/>
        <b>Batch Start:</b> ${res.batch_start || "—"}<br/>
        <b>Batch End:</b> ${res.batch_end || "—"}<br/>
        <b>Total Class Days:</b> ${res.total_class_days || 0}<br/>
        <b>Total Students:</b> ${res.total_students || 0}<br/>
        <b>Total present:</b> ${res.total_present || 0}<br/>
        <b>Total absent:</b> ${res.total_absent || 0}<br/>

        <b>Active Days:</b> ${res.batch_active_days?.join(", ") || "—"}

        
  <div style="margin-top:15px; text-align:right;">
    <button id="downloadPDFBtn" 
      style="background:none; border:none; cursor:pointer;">
      <img src="https://cdn-icons-png.flaticon.com/512/337/337946.png" 
           width="28" title="Download PDF"/>
    </button>
  </div>
      </div>
    `;

      Swal.fire({
        title: "📘 Batch Information",
        html: batchInfoHtml,
        icon: "info",
        confirmButtonText: "OK",
        didRender: () => {
          const btn = document.getElementById("downloadPDFBtn");
          if (btn) {
            btn.addEventListener("click", () => downloadBatchInfoPDF(res));
          }
        },
      });
    } catch (error) {
      Swal.close();
      console.error("Error fetching batch info:", error);
      Swal.fire("Error", "Something went wrong while fetching info", "error");
    }
  };

  const downloadBatchInfoPDF = (res) => {
    const { jsPDF } = require("jspdf");

    const doc = new jsPDF();

    doc.setFontSize(14);
    doc.text("Batch Information Report", 10, 15);
    doc.setFontSize(11);

    let y = 30;

    doc.text(`Batch Name: ${res.batch_name || "—"}`, 10, y);
    y += 8;
    doc.text(`Date Range: ${res.date_range?.join(" to ") || "—"}`, 10, y);
    y += 8;
    doc.text(`Batch Start: ${res.batch_start || "—"}`, 10, y);
    y += 8;
    doc.text(`Batch End: ${res.batch_end || "—"}`, 10, y);
    y += 8;
    doc.text(`Total Class Days: ${res.total_class_days || 0}`, 10, y);
    y += 8;
    doc.text(`Total Students: ${res.total_students || 0}`, 10, y);
    y += 8;
    doc.text(`Present: ${res.total_present || 0}`, 10, y);
    y += 8;
    doc.text(`Absent: ${res.total_absent || 0}`, 10, y);
    y += 8;

    doc.text(`Active Days: ${res.batch_active_days?.join(", ") || "—"}`, 10, y);

    doc.save(`Batch_Info_${res.batch_name || "Report"}.pdf`);
  };

  const downloadAttendanceExcel = async () => {
    try {
      const payload = {
        company_id,
        batch_id: selectedBatch !== "All" ? selectedBatch : null,
        page: page + 1,
        per_page: rowsPerPage,
        search: searchTerm,
        search_by: searchType,
        sort_field: sortField,
        sort_order: sortOrder,
        from_date: dateRange[0] ? dayjs(dateRange[0]).format("YYYY-MM-DD") : "",
        to_date: dateRange[1] ? dayjs(dateRange[1]).format("YYYY-MM-DD") : "",
      };

      const res = await configServ.getAttendanceReport(payload);

      if (!res?.status || !res?.students?.length) {
        Swal.fire("No Data", "No attendance data found", "info");
        return;
      }

      const formattedData = res.students.map((student) => ({
        STUDENT_NAME: student.student_name,
        REG_NO: student.registration_no,
        ROLL_NO: student.roll_no,
        PRESENT: student.present,
        ABSENT: student.absent,
        TOTAL_CLASSES: student.total_classes,
        ATTENDANCE_PERCENTAGE: `${student.attendance_percentage}%`,
      }));

      await downloadExcelReport({
        title: "Attendance Report",
        fileName: "attendance_report.xlsx",
        data: formattedData,
        dateRange: res.date_range,
        extraInfo: `Batch Name: ${res.batch_name || "—"}`,
        columns: [
          "STUDENT_NAME",
          "REG_NO",
          "ROLL_NO",
          "PRESENT",
          "ABSENT",
          "TOTAL_CLASSES",
          "ATTENDANCE_PERCENTAGE",
        ],
      });
    } catch (error) {
      console.error("Download Excel Error:", error);
    }
  };

  const handleDownloadCalender = async (row) => {
    try {
      Swal.fire({
        title: "Fetching Data...",
        text: "Please wait while we prepare your PDF.",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      // 🔹 Fetch batch info first
      const dataToSend = {
        company_id,
        batch_id: selectedBatch !== "All" ? selectedBatch : null,
        page: page + 1,
        per_page: rowsPerPage,
        search: searchTerm,
        search_by: searchType,
        sort_field: sortField,
        sort_order: sortOrder,
        from_date: dateRange[0] ? dayjs(dateRange[0]).format("YYYY-MM-DD") : "",
        to_date: dateRange[1] ? dayjs(dateRange[1]).format("YYYY-MM-DD") : "",
      };

      const res = await configServ.getAttendanceReport(dataToSend);
      Swal.close();

      if (!res?.status) {
        Swal.fire("Error", "Failed to fetch batch details", "error");
        return;
      }

      // 🧾 Create PDF
      const doc = new jsPDF();

      // 🔹 Title
      doc.setFontSize(16);
      doc.text("STUDENT ATTENDANCE REPORT", 55, 15);

      // 🔹 Student Info
      doc.setFontSize(12);
      doc.text(`Student Name: ${row.student_name || "—"}`, 14, 30);
      doc.text(`Registration No: ${row.registration_no || "—"}`, 14, 37);
      doc.text(`Roll No: ${row.roll_no || "—"}`, 14, 44);

      // 🔹 Batch Info
      const batch = res.batch_details || {};
      const startY = 52;
      doc.text(
        `Batch Name: ${batch.name || res.batch_name || "—"}`,
        14,
        startY
      );
      doc.text(
        `Date Range: ${batch.start_date || res.date_range?.[0] || "—"} to ${
          batch.end_date || res.date_range?.[1] || "—"
        }`,
        14,
        startY + 7
      );
      doc.text(
        `No. of Days: ${batch.total_days || res.total_class_days || 0}`,
        14,
        startY + 14
      );

      // 🔹 Attendance Table
      const tableBody = row.calendar.map((entry) => {
        let statusText = "";
        if (entry.status === "present") statusText = "Present";
        else if (entry.status === "absent") statusText = "Absent";
        else if (entry.status === "holiday") statusText = "Holiday";
        else statusText = "Attendance Not Taken";

        return [entry.date, statusText];
      });

      autoTable(doc, {
        head: [["Date", "Status"]],
        body: tableBody,
        startY: startY + 25,
        theme: "grid",
        headStyles: { fillColor: [41, 128, 185] },
      });

      // 🔹 Save file
      const fileName = `${
        row.student_name?.replace(/\s+/g, "_") || "student"
      }_attendance.pdf`;
      doc.save(fileName);

      Swal.fire({
        position: "top-end",
        icon: "success",
        title: "PDF downloaded successfully",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (error) {
      console.error("Error generating calendar PDF:", error);
      Swal.close();
      Swal.fire("Error", "Failed to generate attendance calendar PDF", "error");
    }
  };

  return (
    <>
      <CardHeadMenu title="Attendance Report" isCreate={false} />

      {/* 🔹 Filters */}
      <Box
        sx={{
          mt: 2,
          mb: 3,
          p: 2,
          backgroundColor: "#fff",
          borderRadius: 2,
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          width: "100%",
        }}
      >
        <Grid
          container
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          sx={{ flexWrap: "wrap" }}
        >
          {/* Left Section */}
          <Grid
            item
            xs={12}
            md="auto"
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              alignItems: "center",
            }}
          >
            <Typography variant="subtitle1" fontWeight="bold">
              Total Students: <span style={{ color: "#1976d2" }}>{total}</span>
            </Typography>

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Select Batch</InputLabel>
              <Select
                value={selectedBatch}
                onChange={(e) => setSelectedBatch(e.target.value)}
                label="Select Batch"
              >
                <MenuItem value="All">Select Batches</MenuItem>
                {batches.map((batch) => (
                  <MenuItem key={batch.id} value={batch.id}>
                    {batch.item_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Search By</InputLabel>
              <Select
                value={searchType}
                onChange={(e) => setSearchType(e.target.value)}
                label="Search By"
              >
                <MenuItem value="full_name">Student Name</MenuItem>
                {/* <MenuItem value="email">Email</MenuItem> */}
              </Select>
            </FormControl>

            <TextField
              size="small"
              label="Search"
              variant="outlined"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ minWidth: 180 }}
            />

            <IconButton
              color="secondary"
              size="small"
              onClick={handleReset}
              sx={{ border: "1px solid #ddd" }}
            >
              <RestartAltIcon />
            </IconButton>

            <CustomDateRangePicker value={dateRange} onChange={setDateRange} />
            <IconButton
              onClick={() => downloadAttendanceExcel()}
              title="Download Excel"
              sx={{
                p: 0,
                borderRadius: "8px",
                overflow: "hidden",
              }}
            >
              <Box
                component="img"
                src={ExcelImg}
                alt="Download Excel"
                sx={{
                  width: 36,
                  height: 36,
                  objectFit: "cover",
                  borderRadius: "8px",
                  transition: "transform 0.2s ease",
                  "&:hover": { transform: "scale(1.1)" },
                }}
              />
            </IconButton>
            <IconButton
              onClick={() => downloadAttendancePDF()}
              title="Download pdf"
              sx={{
                p: 0,
                borderRadius: "8px",
                overflow: "hidden",
              }}
            >
              <Box
                component="img"
                src={pdfImg}
                alt="Download pdf"
                sx={{
                  width: 36,
                  height: 36,
                  objectFit: "cover",
                  borderRadius: "8px",
                  transition: "transform 0.2s ease",
                  "&:hover": { transform: "scale(1.1)" },
                }}
              />
            </IconButton>
            <IconButton
              onClick={() => handleInfo()}
              title="Details"
              sx={{
                p: 0,
                borderRadius: "8px",
                overflow: "hidden",
              }}
            >
              <Box
                component="img"
                src={info}
                alt="Details"
                sx={{
                  width: 36,
                  height: 36,
                  objectFit: "cover",
                  borderRadius: "8px",
                  transition: "transform 0.2s ease",
                  "&:hover": { transform: "scale(1.1)" },
                }}
              />
            </IconButton>
          </Grid>
        </Grid>
      </Box>

      {/* 🔹 Table */}
      <TableContainer
        component={Paper}
        sx={{ display: { xs: "none", md: "block" }, mt: 2 }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell style={headerCellStyle}>#</TableCell>
              {headers.map((header, index) => {
                const isActive = sortField === header.value;
                return (
                  <TableCell
                    key={index}
                    style={headerCellStyle}
                    onClick={() => handleSort(header.value)}
                    sx={{
                      cursor: ![
                        "present",
                        "absent",
                        "total_classes",
                        "attendance_percentage",
                      ].includes(header.value)
                        ? "pointer"
                        : "default",
                    }}
                  >
                    {header.label}
                    {![
                      "present",
                      "absent",
                      "total_classes",
                      "attendance_percentage",
                      //   "profile",
                    ].includes(header.value) && (
                      <span style={{ marginLeft: 4 }}>
                        {isActive ? (sortOrder === "asc" ? "▲" : "▼") : "⇅"}
                      </span>
                    )}
                  </TableCell>
                );
              })}
              <TableCell style={headerCellStyle}>Calendar</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={headers.length + 2} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : students.length === 0 ? (
              <TableRow>
                <TableCell colSpan={headers.length + 2} align="center">
                  <Typography color="error">
                    <b>Select Any Batch</b>
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              students.map((row, index) => (
                <TableRow key={index} sx={rowStyle}>
                  <TableCell sx={cellStyle}>
                    {page * rowsPerPage + index + 1}
                  </TableCell>
                  <TableCell sx={cellStyle}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        component="img"
                        src={row.profile || ""}
                        alt={row.student_name}
                        onError={(e) => (e.target.style.display = "none")} // hide broken img
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: "50%",
                          objectFit: "cover",
                          display: row.profile ? "block" : "none",
                        }}
                      />
                      {!row.profile && (
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: "50%",
                            backgroundColor: "#f0f0f0",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 18,
                          }}
                        >
                          👤
                        </Box>
                      )}
                      <Typography variant="body2" sx={{ ml: 1 }}>
                        {row.student_name || "—"}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={cellStyle}>{row.registration_no}</TableCell>
                  <TableCell sx={cellStyle}>{row.roll_no}</TableCell>

                  <TableCell sx={cellStyle}>{row.present}</TableCell>
                  <TableCell sx={cellStyle}>{row.absent}</TableCell>
                  <TableCell sx={cellStyle}>{row.total_classes}</TableCell>
                  <TableCell sx={cellStyle}>
                    {row.attendance_percentage}%
                  </TableCell>
                  <TableCell sx={cellStyle}>
                    <Box
                      component="img"
                      src={attImage}
                      alt="View Attendance"
                      onClick={() => handleViewCalendar(row.calendar)}
                      sx={{
                        width: 34,
                        height: 34,
                        objectFit: "contain",
                        cursor: "pointer",
                        borderRadius: "8px",
                        transition: "transform 0.2s ease, box-shadow 0.2s ease",
                        "&:hover": {
                          transform: "scale(1.1)",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                        },
                      }}
                    />
                    <Box
                      component="img"
                      src={pdfImg}
                      alt="Attendance"
                      onClick={() => handleDownloadCalender(row)}
                      sx={{
                        width: 34,
                        height: 34,
                        objectFit: "contain",
                        cursor: "pointer",
                        borderRadius: "8px",
                        transition: "transform 0.2s ease, box-shadow 0.2s ease",
                        "&:hover": {
                          transform: "scale(1.1)",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                        },
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        <TablePagination
          rowsPerPageOptions={[100, 150, 200]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {/* ✅ CARD VIEW (Mobile only) */}
      <Box sx={{ display: { xs: "block", md: "none" }, mt: 2 }}>
        {loading ? (
          <Box textAlign="center" py={4}>
            <CircularProgress />
          </Box>
        ) : students.length === 0 ? (
          <Typography align="center" color="error">
            <b>No Data Found</b>
          </Typography>
        ) : (
          students.map((row, index) => (
            <Card
              key={index}
              sx={{
                mb: 2,
                borderRadius: 2,
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              }}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mb: 1,
                    gap: 1,
                  }}
                >
                  <Box
                    component="img"
                    src={row.profile || ""}
                    alt={row.student_name}
                    onError={(e) => (e.target.style.display = "none")}
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      objectFit: "cover",
                      display: row.profile ? "block" : "none",
                    }}
                  />
                  {!row.profile && (
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        backgroundColor: "#f0f0f0",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 18,
                      }}
                    >
                      👤
                    </Box>
                  )}
                  <Typography variant="subtitle1" fontWeight={600}>
                    {row.student_name}
                  </Typography>
                </Box>

                <Divider sx={{ mb: 1 }} />

                <Typography variant="body2">
                  <b>Reg No:</b> {row.registration_no}
                </Typography>
                <Typography variant="body2">
                  <b>Roll No:</b> {row.roll_no}
                </Typography>
                <Typography variant="body2">
                  <b>Present:</b> {row.present} | <b>Absent:</b> {row.absent}
                </Typography>
                <Typography variant="body2">
                  <b>Total Classes:</b> {row.total_classes}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <b>Attendance:</b> {row.attendance_percentage}%
                </Typography>

                <Box textAlign="right">
                  <Box
                    component="img"
                    src={attImage}
                    alt="View Calendar"
                    onClick={() => handleViewCalendar(row.calendar)}
                    sx={{
                      width: 36,
                      height: 36,
                      cursor: "pointer",
                      borderRadius: "8px",
                      transition: "transform 0.2s ease",
                      "&:hover": { transform: "scale(1.1)" },
                    }}
                  />
                  <Box
                    component="img"
                    src={pdfImg}
                    alt="Attendance"
                    onClick={() => handleDownloadCalender(row)}
                    sx={{
                      width: 34,
                      height: 34,
                      objectFit: "contain",
                      cursor: "pointer",
                      borderRadius: "8px",
                      transition: "transform 0.2s ease, box-shadow 0.2s ease",
                      "&:hover": {
                        transform: "scale(1.1)",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                      },
                    }}
                  />
                </Box>
              </CardContent>
            </Card>
          ))
        )}
      </Box>
      <Box sx={{ display: { xs: "block", md: "none" } }}>
        <TablePagination
          rowsPerPageOptions={[100, 150, 200]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Box>

      <AttendanceCalendarDialog
        calendarDialog={calendarDialog}
        setCalendarDialog={setCalendarDialog}
        selectedCalendar={selectedCalendar}
        batchDays={batchDays}
        dayRange={dayRange}
      />

      {/* 🔹 Calendar Dialog */}
      {/* <Dialog
        open={calendarDialog}
        onClose={() => setCalendarDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Attendance Calendar</DialogTitle>
        <DialogContent>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {selectedCalendar.map((day, i) => (
                <TableRow key={i}>
                  <TableCell>{day.date}</TableCell>
                  <TableCell>
                    <Chip
                      label={day.status}
                      color={
                        day.status === "present"
                          ? "success"
                          : day.status === "absent"
                          ? "error"
                          : "default"
                      }
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCalendarDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog> */}
    </>
  );
};

export default AttendanceReport;
